'use client';
import React, { useState } from 'react';

import PublicNavbar from '../landing/components/PublicNavbar';
import PublicFooter from '../landing/components/PublicFooter';

const sections = [
  { id: 'overview', title: 'Overview' },
  { id: 'data-collected', title: 'Data We Collect' },
  { id: 'how-we-use', title: 'How We Use Your Data' },
  { id: 'sharing', title: 'Data Sharing' },
  { id: 'storage', title: 'Data Storage & Security' },
  { id: 'rights', title: 'Your Rights' },
  { id: 'cookies', title: 'Cookies & Tracking' },
  { id: 'children', title: 'Children\'s Privacy' },
  { id: 'changes', title: 'Policy Changes' },
  { id: 'contact', title: 'Contact Us' },
];

export default function PrivacyPolicyPage() {
  const [activeSection, setActiveSection] = useState('overview');

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <PublicNavbar />
      {/* Hero */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628] via-[#0D2B3E] to-[#0A1628]" />
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-1/3 w-[500px] h-[500px] rounded-full bg-[#0D9488]/10 blur-[120px]" />
          <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] rounded-full bg-[#2563EB]/10 blur-[100px]" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-5">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#0D9488]/15 border border-[#0D9488]/30">
            <svg className="w-4 h-4 text-[#4DD9CC]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <span className="text-[#4DD9CC] text-sm font-bold uppercase tracking-widest">Privacy Policy</span>
          </div>
          <h1 className="text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-tight">
            Your privacy is not<br />
            <span className="text-[#4DD9CC]">a feature. It's a right.</span>
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed">
            We collect only what we need, protect it like it's our own, and never sell it. Here's exactly what we do — in plain language.
          </p>
          <div className="flex items-center justify-center gap-6 pt-2">
            <div className="text-center">
              <div className="text-2xl font-extrabold text-white">Last Updated</div>
              <div className="text-sm text-slate-400">July 14, 2026</div>
            </div>
            <div className="w-px h-10 bg-white/20" />
            <div className="text-center">
              <div className="text-2xl font-extrabold text-white">Effective</div>
              <div className="text-sm text-slate-400">July 14, 2026</div>
            </div>
            <div className="w-px h-10 bg-white/20" />
            <div className="text-center">
              <div className="text-2xl font-extrabold text-white">Version</div>
              <div className="text-sm text-slate-400">2.1</div>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-[#F8FAFC] to-transparent" />
      </section>
      {/* Quick summary cards */}
      <section className="py-10 bg-[#F8FAFC]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: '🚫', title: 'We Never Sell Your Data', desc: 'Your personal information is never sold to third parties, advertisers, or data brokers. Period.', color: 'bg-red-50 border-red-100' },
              { icon: '🔐', title: 'End-to-End Encryption', desc: 'All sensitive data — payments, IDs, messages — is encrypted in transit and at rest using AES-256.', color: 'bg-teal-50 border-teal-100' },
              { icon: '🗑️', title: 'Right to Delete', desc: 'Request deletion of your account and all associated data at any time. We process it within 30 days.', color: 'bg-blue-50 border-blue-100' },
              { icon: '🇰🇪', title: 'Kenya Data Laws', desc: 'We comply fully with the Kenya Data Protection Act 2019 and applicable international standards.', color: 'bg-amber-50 border-amber-100' },
            ]?.map((card) => (
              <div key={card?.title} className={`rounded-2xl p-6 border ${card?.color} hover:-translate-y-1 hover:shadow-lg transition-all duration-300`}>
                <div className="text-3xl mb-3">{card?.icon}</div>
                <h3 className="font-extrabold text-[#0F172A] text-sm mb-2">{card?.title}</h3>
                <p className="text-xs text-slate-600 leading-relaxed">{card?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Main content */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-12">
            {/* Sticky sidebar nav */}
            <aside className="hidden lg:block w-64 shrink-0">
              <div className="sticky top-24 rounded-2xl border border-slate-100 bg-[#F8FAFC] p-5">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">Contents</p>
                <nav className="space-y-1">
                  {sections?.map((s) => (
                    <a
                      key={s?.id}
                      href={`#${s?.id}`}
                      onClick={() => setActiveSection(s?.id)}
                      className={`block px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                        activeSection === s?.id
                          ? 'bg-[#0D9488] text-white'
                          : 'text-slate-600 hover:text-[#0D9488] hover:bg-teal-50'
                      }`}
                    >
                      {s?.title}
                    </a>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Policy content */}
            <div className="flex-1 max-w-3xl space-y-14">

              {/* Overview */}
              <div id="overview" className="scroll-mt-24">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center text-xl">🌍</div>
                  <h2 className="text-2xl font-extrabold text-[#0F172A]">Overview</h2>
                </div>
                <div className="prose prose-slate max-w-none space-y-4">
                  <p className="text-slate-600 leading-relaxed">
                    MuyuRide Limited ("MuyuRide", "we", "us", or "our") operates Kenya's leading peer-to-peer parcel delivery marketplace at <strong>muyuride.co.ke</strong> and through our mobile applications. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform.
                  </p>
                  <p className="text-slate-600 leading-relaxed">
                    This policy applies to all users of MuyuRide — senders, travelers, storage providers, station moderators, and visitors. By using MuyuRide, you agree to the collection and use of information in accordance with this policy.
                  </p>
                  <div className="rounded-xl bg-teal-50 border border-teal-200 p-5">
                    <p className="text-sm font-bold text-[#0D9488] mb-2">🇰🇪 Legal Basis</p>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      We operate under the <strong>Kenya Data Protection Act, 2019 (No. 24 of 2019)</strong> and are registered with the Office of the Data Protection Commissioner (ODPC). Our Data Protection Officer can be reached at <strong>dpo@muyuride.co.ke</strong>.
                    </p>
                  </div>
                </div>
              </div>

              {/* Data Collected */}
              <div id="data-collected" className="scroll-mt-24">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center text-xl">📋</div>
                  <h2 className="text-2xl font-extrabold text-[#0F172A]">Data We Collect</h2>
                </div>
                <div className="space-y-5">
                  {[
                    {
                      title: 'Account & Identity Information',
                      items: ['Full name, email address, phone number', 'National ID number (for traveler verification)', 'Student ID and institution (for student KYC)', 'Profile photo (optional)', 'Date of birth (for age verification)'],
                      why: 'Required to create your account and verify your identity on the platform.',
                    },
                    {
                      title: 'Delivery & Transaction Data',
                      items: ['Parcel descriptions, weights, and declared values', 'Pickup and destination addresses', 'Delivery photos and proof of delivery', 'Order history and trip records', 'Bid amounts and accepted prices'],
                      why: 'Required to facilitate deliveries and resolve disputes.',
                    },
                    {
                      title: 'Payment Information',
                      items: ['M-Pesa phone number and transaction references', 'Stripe payment tokens (we never store raw card numbers)', 'Escrow transaction history', 'Payout records and earnings statements'],
                      why: 'Required to process payments, hold escrow, and release funds.',
                    },
                    {
                      title: 'Location Data',
                      items: ['Real-time GPS coordinates during active deliveries (travelers only, with consent)', 'City-level location for route matching', 'Pickup and drop-off coordinates'],
                      why: 'Required for live tracking and route matching. Location tracking stops when delivery is complete.',
                    },
                    {
                      title: 'Device & Usage Data',
                      items: ['IP address, browser type, and operating system', 'Pages visited and features used', 'Session duration and click patterns', 'Device identifiers (mobile app only)', 'Error logs and crash reports'],
                      why: 'Used to improve platform performance, detect fraud, and debug issues.',
                    },
                    {
                      title: 'Communications',
                      items: ['Messages between senders and travelers (encrypted)', 'Support tickets and chat logs', 'Dispute evidence and uploaded files', 'Review and rating content'],
                      why: 'Required for dispute resolution and platform safety.',
                    },
                  ]?.map((cat) => (
                    <div key={cat?.title} className="rounded-xl border border-slate-100 bg-[#F8FAFC] p-6">
                      <h3 className="font-extrabold text-[#0F172A] mb-3">{cat?.title}</h3>
                      <ul className="space-y-1.5 mb-4">
                        {cat?.items?.map((item) => (
                          <li key={item} className="flex items-start gap-2 text-sm text-slate-600">
                            <svg className="w-4 h-4 text-[#0D9488] mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                            </svg>
                            {item}
                          </li>
                        ))}
                      </ul>
                      <div className="flex items-start gap-2 p-3 rounded-lg bg-white border border-slate-100">
                        <span className="text-xs font-bold text-[#0D9488] shrink-0">WHY:</span>
                        <span className="text-xs text-slate-500">{cat?.why}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* How We Use */}
              <div id="how-we-use" className="scroll-mt-24">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center text-xl">⚙️</div>
                  <h2 className="text-2xl font-extrabold text-[#0F172A]">How We Use Your Data</h2>
                </div>
                <div className="space-y-4">
                  {[
                    { purpose: 'Platform Operations', desc: 'Matching senders with travelers, processing orders, managing escrow payments, and facilitating deliveries across Kenya.' },
                    { purpose: 'Identity Verification & Trust', desc: 'Verifying traveler identities, processing guarantor information, running KYC checks, and maintaining trust scores to keep the platform safe.' },
                    { purpose: 'Payments & Escrow', desc: 'Processing M-Pesa and Stripe payments, holding funds in escrow until delivery confirmation, releasing earnings to travelers, and issuing refunds.' },
                    { purpose: 'Safety & Fraud Prevention', desc: 'Detecting suspicious activity, investigating disputes, flagging fraudulent accounts, and protecting users from scams.' },
                    { purpose: 'Customer Support', desc: 'Responding to support requests, resolving disputes, investigating complaints, and improving our help documentation.' },
                    { purpose: 'Platform Improvement', desc: 'Analyzing usage patterns to improve features, fix bugs, optimize performance, and develop new services.' },
                    { purpose: 'Legal Compliance', desc: 'Complying with Kenyan law, responding to lawful government requests, and maintaining records as required by financial regulations.' },
                    { purpose: 'Communications', desc: 'Sending delivery notifications, account alerts, security warnings, and (with your consent) product updates and promotions.' },
                  ]?.map((item, i) => (
                    <div key={item?.purpose} className="flex gap-4 p-5 rounded-xl border border-slate-100 hover:border-teal-200 hover:bg-teal-50/30 transition-all duration-200">
                      <div className="w-8 h-8 rounded-lg bg-[#0D9488] text-white text-sm font-extrabold flex items-center justify-center shrink-0">
                        {i + 1}
                      </div>
                      <div>
                        <h3 className="font-extrabold text-[#0F172A] text-sm mb-1">{item?.purpose}</h3>
                        <p className="text-sm text-slate-600 leading-relaxed">{item?.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Data Sharing */}
              <div id="sharing" className="scroll-mt-24">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center text-xl">🤝</div>
                  <h2 className="text-2xl font-extrabold text-[#0F172A]">Data Sharing</h2>
                </div>
                <div className="rounded-xl bg-red-50 border border-red-200 p-5 mb-6">
                  <p className="text-sm font-extrabold text-red-700 mb-1">🚫 We never sell your data.</p>
                  <p className="text-sm text-red-600">MuyuRide does not sell, rent, or trade your personal information to third parties for their marketing purposes. Ever.</p>
                </div>
                <div className="space-y-4">
                  <p className="text-slate-600 leading-relaxed">We share data only in these specific circumstances:</p>
                  {[
                    { who: 'Other Platform Users', what: 'When you accept a delivery, your name, phone number, and profile photo are shared with the counterparty to facilitate the delivery. Your full ID number is never shared with other users.' },
                    { who: 'Payment Processors', what: 'Safaricom (M-Pesa) and Stripe receive the minimum data required to process payments. We share transaction amounts, phone numbers, and payment tokens — never raw card data.' },
                    { who: 'Identity Verification Partners', what: 'We use licensed KYC providers to verify national IDs and student documents. These partners are bound by strict data processing agreements and Kenyan data protection law.' },
                    { who: 'Cloud Infrastructure', what: 'We use Supabase (hosted on AWS in the EU) for database and storage. All data is encrypted at rest and in transit. Supabase processes data only on our instructions.' },
                    { who: 'Legal Authorities', what: 'We disclose data to law enforcement or government bodies only when required by Kenyan law, a valid court order, or to protect the safety of our users. We will notify you of such requests where legally permitted.' },
                    { who: 'Business Transfers', what: 'If MuyuRide is acquired or merges with another company, your data may be transferred as part of that transaction. We will notify you 30 days in advance and you may request deletion before the transfer.' },
                  ]?.map((item) => (
                    <div key={item?.who} className="rounded-xl border border-slate-100 p-5">
                      <h3 className="font-extrabold text-[#0F172A] text-sm mb-2">{item?.who}</h3>
                      <p className="text-sm text-slate-600 leading-relaxed">{item?.what}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Storage & Security */}
              <div id="storage" className="scroll-mt-24">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center text-xl">🔐</div>
                  <h2 className="text-2xl font-extrabold text-[#0F172A]">Data Storage & Security</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                  {[
                    { icon: '🔒', title: 'AES-256 Encryption', desc: 'All data encrypted at rest using industry-standard AES-256 encryption.' },
                    { icon: '🌐', title: 'TLS 1.3 in Transit', desc: 'All data transmitted over TLS 1.3 — the strongest available transport encryption.' },
                    { icon: '🏦', title: 'Secure Escrow', desc: 'Payment data isolated in PCI-DSS compliant infrastructure, never stored on our servers.' },
                    { icon: '🔑', title: '2FA Available', desc: 'Two-factor authentication available for all accounts via TOTP authenticator apps.' },
                  ]?.map((item) => (
                    <div key={item?.title} className="rounded-xl bg-[#F8FAFC] border border-slate-100 p-5 flex gap-3">
                      <span className="text-2xl">{item?.icon}</span>
                      <div>
                        <h3 className="font-extrabold text-[#0F172A] text-sm mb-1">{item?.title}</h3>
                        <p className="text-xs text-slate-500 leading-relaxed">{item?.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="space-y-4 text-slate-600">
                  <p className="leading-relaxed"><strong>Data Retention:</strong> We retain your account data for as long as your account is active. After account deletion, we retain anonymized transaction records for 7 years as required by Kenyan financial regulations. All personally identifiable information is deleted within 30 days of your deletion request.</p>
                  <p className="leading-relaxed"><strong>Data Location:</strong> Your data is stored on servers located in the European Union (AWS eu-west-1). We have a Data Processing Agreement with Supabase that ensures EU GDPR-equivalent protections apply to your data.</p>
                  <p className="leading-relaxed"><strong>Breach Notification:</strong> In the event of a data breach affecting your personal information, we will notify you and the ODPC within 72 hours of becoming aware of the breach, as required by the Kenya Data Protection Act.</p>
                </div>
              </div>

              {/* Your Rights */}
              <div id="rights" className="scroll-mt-24">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center text-xl">⚖️</div>
                  <h2 className="text-2xl font-extrabold text-[#0F172A]">Your Rights</h2>
                </div>
                <p className="text-slate-600 mb-5 leading-relaxed">Under the Kenya Data Protection Act 2019, you have the following rights regarding your personal data:</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { right: 'Right to Access', desc: 'Request a copy of all personal data we hold about you. We provide this within 21 days.', action: 'Email support@muyulink.com' },
                    { right: 'Right to Rectification', desc: 'Correct inaccurate or incomplete personal data. Most data can be updated directly in your profile settings.', action: 'Update in Profile Settings' },
                    { right: 'Right to Erasure', desc: 'Request deletion of your account and personal data. We process within 30 days (subject to legal retention requirements).', action: 'Request via Settings' },
                    { right: 'Right to Portability', desc: 'Receive your data in a structured, machine-readable format (JSON/CSV) to transfer to another service.', action: 'Email support@muyulink.com' },
                    { right: 'Right to Object', desc: 'Object to processing of your data for marketing purposes. You can opt out of all marketing communications instantly.', action: 'Unsubscribe link in emails' },
                    { right: 'Right to Restrict Processing', desc: 'Request that we limit how we use your data while a dispute or complaint is being investigated.', action: 'Email support@muyulink.com' },
                  ]?.map((item) => (
                    <div key={item?.right} className="rounded-xl border border-slate-100 bg-[#F8FAFC] p-5">
                      <h3 className="font-extrabold text-[#0F172A] text-sm mb-2">{item?.right}</h3>
                      <p className="text-xs text-slate-600 leading-relaxed mb-3">{item?.desc}</p>
                      <span className="inline-block px-3 py-1 rounded-full bg-teal-100 text-[#0D9488] text-xs font-bold">{item?.action}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-5 rounded-xl bg-amber-50 border border-amber-200 p-5">
                  <p className="text-sm font-bold text-amber-800 mb-1">📋 How to Exercise Your Rights</p>
                  <p className="text-sm text-amber-700">Email our Data Protection Officer at <strong>support@muyulink.com</strong> with your request. Include your registered email address and a description of your request. We will respond within 21 days. If you are unsatisfied with our response, you may lodge a complaint with the <strong>Office of the Data Protection Commissioner (ODPC)</strong> at odpc.go.ke.</p>
                </div>
              </div>

              {/* Cookies */}
              <div id="cookies" className="scroll-mt-24">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center text-xl">🍪</div>
                  <h2 className="text-2xl font-extrabold text-[#0F172A]">Cookies & Tracking</h2>
                </div>
                <div className="space-y-4 text-slate-600">
                  <p className="leading-relaxed">We use cookies and similar tracking technologies to operate our platform, remember your preferences, and understand how you use MuyuRide.</p>
                  <div className="space-y-3">
                    {[
                      { type: 'Essential Cookies', desc: 'Required for the platform to function. These include session cookies, authentication tokens, and security cookies. Cannot be disabled.', required: true },
                      { type: 'Functional Cookies', desc: 'Remember your language preference, notification settings, and UI preferences. Disabled by default — enabled when you save preferences.', required: false },
                      { type: 'Analytics Cookies', desc: 'Help us understand how users navigate MuyuRide so we can improve the experience. We use anonymized, aggregated data only. Opt-out available.', required: false },
                      { type: 'Marketing Cookies', desc: 'Used to show relevant MuyuRide ads on other platforms. We do not use third-party advertising cookies. Our own retargeting is opt-in only.', required: false },
                    ]?.map((cookie) => (
                      <div key={cookie?.type} className="flex items-start gap-4 p-4 rounded-xl border border-slate-100">
                        <div className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${cookie?.required ? 'bg-[#0D9488]' : 'bg-slate-200'}`}>
                          {cookie?.required ? (
                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                          ) : (
                            <div className="w-2 h-2 rounded-full bg-slate-400" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-extrabold text-[#0F172A] text-sm">{cookie?.type}</h3>
                            {cookie?.required && <span className="px-2 py-0.5 rounded-full bg-teal-100 text-[#0D9488] text-xs font-bold">Required</span>}
                          </div>
                          <p className="text-xs text-slate-500 leading-relaxed">{cookie?.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Children */}
              <div id="children" className="scroll-mt-24">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-pink-100 flex items-center justify-center text-xl">👶</div>
                  <h2 className="text-2xl font-extrabold text-[#0F172A]">Children's Privacy</h2>
                </div>
                <div className="rounded-xl bg-pink-50 border border-pink-200 p-6">
                  <p className="text-slate-600 leading-relaxed">MuyuRide is not intended for use by individuals under the age of 18. We do not knowingly collect personal information from children under 18. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately at <strong>dpo@muyuride.co.ke</strong>. We will delete such information within 7 days of receiving your request.</p>
                </div>
              </div>

              {/* Changes */}
              <div id="changes" className="scroll-mt-24">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-xl">📝</div>
                  <h2 className="text-2xl font-extrabold text-[#0F172A]">Policy Changes</h2>
                </div>
                <div className="space-y-4 text-slate-600">
                  <p className="leading-relaxed">We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. When we make material changes, we will:</p>
                  <ul className="space-y-2">
                    {[
                      'Send you an email notification at least 14 days before the changes take effect',
                      'Display a prominent notice on the MuyuRide platform',
                      'Update the "Last Updated" date at the top of this policy',
                      'Maintain an archive of previous versions accessible on request',
                    ]?.map((item) => (
                      <li key={item} className="flex items-start gap-2 text-sm">
                        <svg className="w-4 h-4 text-[#0D9488] mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                        {item}
                      </li>
                    ))}
                  </ul>
                  <p className="leading-relaxed">Your continued use of MuyuRide after the effective date of any changes constitutes your acceptance of the updated policy. If you do not agree to the changes, you may delete your account before the effective date.</p>
                </div>
              </div>

              {/* Contact */}
              <div id="contact" className="scroll-mt-24">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center text-xl">📬</div>
                  <h2 className="text-2xl font-extrabold text-[#0F172A]">Contact Us</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="rounded-xl bg-[#F8FAFC] border border-slate-100 p-6 space-y-3">
                    <h3 className="font-extrabold text-[#0F172A]">Data Protection Officer</h3>
                    <div className="space-y-2 text-sm text-slate-600">
                      <p><strong>Email:</strong> support@muyulink.com</p>
                      <p><strong>Response time:</strong> Within 21 days</p>
                      <p><strong>For:</strong> Data access, deletion, portability requests</p>
                    </div>
                  </div>
                  <div className="rounded-xl bg-[#F8FAFC] border border-slate-100 p-6 space-y-3">
                    <h3 className="font-extrabold text-[#0F172A]">General Privacy Queries</h3>
                    <div className="space-y-2 text-sm text-slate-600">
                      <p><strong>Email:</strong> support@muyulink.com</p>
                      <p><strong>Phone / WhatsApp:</strong> 0726 924 637</p>
                      <p><strong>Address:</strong> Nyakio House, 1st Floor, Room 13, Nairobi, Kenya</p>
                      <p><strong>For:</strong> General questions about this policy</p>
                    </div>
                  </div>
                </div>
                <div className="mt-5 rounded-xl bg-teal-50 border border-teal-200 p-5 flex items-start gap-4">
                  <span className="text-2xl">🏛️</span>
                  <div>
                    <p className="font-extrabold text-[#0F172A] text-sm mb-1">Regulatory Authority</p>
                    <p className="text-sm text-slate-600">If you are not satisfied with our response to your privacy request, you have the right to lodge a complaint with the <strong>Office of the Data Protection Commissioner (ODPC)</strong>. Visit <strong>odpc.go.ke</strong> or call <strong>+254 20 2628 000</strong>.</p>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>
      <PublicFooter />
    </div>
  );
}
